import { useMemo } from 'react';
import { FunnelIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  FILTER_TYPES,
  DATE_COLUMN_TYPES,
  DATE_RANGE_PRESETS,
  extractColumnsFromSQL,
  inferColumnType,
  findSourceTableForColumn,
} from '../../utils/chartFilterUtils';

/**
 * Reusable filter configuration UI.
 * Used by both ChartPreview (Add Chart) and EditChartModal (Edit Chart).
 *
 * @param {string}   query                - SQL query to parse columns from
 * @param {object}   schema               - Optional schema { tables: [{ columns: [...] }] }
 * @param {array}    filterConfig          - Current filter configuration array
 * @param {function} onFilterConfigChange  - Callback to update filter config in parent
 */
export default function FilterConfigurator({ query, schema, filterConfig, onFilterConfigChange }) {
  const availableColumns = useMemo(() => {
    const parsedColumns = extractColumnsFromSQL(query);
    const schemaColumns = schema?.tables?.[0]?.columns?.map(col => ({
      name: col.name,
      expression: col.name,
      isAlias: false,
      schemaType: col.type
    })) || [];

    const columnNames = new Set(parsedColumns.map(c => c.name.toLowerCase()));
    const merged = [...parsedColumns];
    schemaColumns.forEach(sc => {
      if (!columnNames.has(sc.name.toLowerCase())) {
        merged.push(sc);
      }
    });
    return merged;
  }, [query, schema]);

  const availableForFiltering = useMemo(() => {
    const configuredColumns = new Set(filterConfig.map(f => f.column.toLowerCase()));
    return availableColumns.filter(c => !configuredColumns.has(c.name.toLowerCase()));
  }, [availableColumns, filterConfig]);

  const addFilter = (column) => {
    const { filterType, columnType } = inferColumnType(column.name, column.type);
    const sourceTable = findSourceTableForColumn(column.name, query);

    const newFilter = {
      id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      column: column.name,
      label: column.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: filterType,
      columnType,
      enabled: true,
      defaultRange: filterType === 'daterange' ? 'last30days' : undefined,
      sourceColumn: column.isAlias ? column.expression : undefined,
      sourceTable: sourceTable || undefined
    };
    onFilterConfigChange([...filterConfig, newFilter]);
  };

  const updateFilter = (filterId, updates) => {
    onFilterConfigChange(filterConfig.map(f => f.id === filterId ? { ...f, ...updates } : f));
  };

  const removeFilter = (filterId) => {
    onFilterConfigChange(filterConfig.filter(f => f.id !== filterId));
  };

  const toggleFilter = (filterId) => {
    onFilterConfigChange(filterConfig.map(f => f.id === filterId ? { ...f, enabled: !f.enabled } : f));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white">
            Configure Filters
          </h3>
          <p className="text-sm text-surface-500 mt-1">
            Choose which columns can be filtered and set their filter type
          </p>
        </div>
      </div>

      {/* Add Filter Section */}
      <div className="bg-surface-50 dark:bg-surface-800 rounded-xl p-4">
        <label className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3 block">
          Add Filter from Query Columns
        </label>

        {availableForFiltering.length === 0 && availableColumns.length === 0 ? (
          <p className="text-sm text-surface-500 italic">
            No columns detected. Make sure the chart has a SQL query.
          </p>
        ) : availableForFiltering.length === 0 ? (
          <p className="text-sm text-surface-500 italic">
            All available columns are already configured as filters.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableForFiltering.map((col, idx) => (
              <button
                key={idx}
                onClick={() => addFilter(col)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-lg text-sm text-surface-700 dark:text-surface-300 hover:bg-primary-50 hover:border-primary-300 dark:hover:bg-primary-900/30 dark:hover:border-primary-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                {col.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Configured Filters */}
      {filterConfig.length > 0 && (
        <div className="space-y-4">
          <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
            Configured Filters ({filterConfig.length})
          </label>

          <div className="space-y-3">
            {filterConfig.map((filter) => (
              <div
                key={filter.id}
                className={`bg-white dark:bg-surface-800 border rounded-xl p-4 transition-all ${
                  filter.enabled
                    ? 'border-primary-200 dark:border-primary-800 shadow-sm'
                    : 'border-surface-200 dark:border-surface-700 opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Enable/Disable Toggle */}
                  <button
                    onClick={() => toggleFilter(filter.id)}
                    className={`mt-1 relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                      filter.enabled ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        filter.enabled ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>

                  {/* Filter Configuration */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-surface-500 mb-1 block">Column</label>
                      <div className="text-sm font-mono bg-surface-50 dark:bg-surface-900 px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700">
                        {filter.column}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-surface-500 mb-1 block">Display Label</label>
                      <input
                        type="text"
                        value={filter.label}
                        onChange={(e) => updateFilter(filter.id, { label: e.target.value })}
                        className="w-full text-sm border border-surface-200 dark:border-surface-700 rounded px-2 py-1.5 bg-white dark:bg-surface-900"
                        placeholder="Filter label"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-surface-500 mb-1 block">Filter Type</label>
                      <select
                        value={filter.type}
                        onChange={(e) => updateFilter(filter.id, {
                          type: e.target.value,
                          ...(e.target.value === 'daterange' ? { defaultRange: 'last30days' } : {})
                        })}
                        className="w-full text-sm border border-surface-200 dark:border-surface-700 rounded px-2 py-1.5 bg-white dark:bg-surface-900"
                      >
                        {FILTER_TYPES.map(ft => (
                          <option key={ft.id} value={ft.id}>
                            {ft.icon} {ft.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {filter.type === 'daterange' ? (
                      <div>
                        <label className="text-xs text-surface-500 mb-1 block">Column Type</label>
                        <select
                          value={filter.columnType || 'STRING'}
                          onChange={(e) => updateFilter(filter.id, { columnType: e.target.value })}
                          className="w-full text-sm border border-surface-200 dark:border-surface-700 rounded px-2 py-1.5 bg-white dark:bg-surface-900"
                        >
                          {DATE_COLUMN_TYPES.map(ct => (
                            <option key={ct.id} value={ct.id}>
                              {ct.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="flex items-end" />
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => removeFilter(filter.id)}
                    className="mt-1 p-1.5 text-surface-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove filter"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Additional options for daterange */}
                {filter.type === 'daterange' && filter.enabled && (
                  <div className="mt-3 pt-3 border-t border-surface-100 dark:border-surface-700 space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="text-xs text-surface-500">Default Range:</label>
                      <div className="flex flex-wrap gap-2">
                        {DATE_RANGE_PRESETS.map(preset => (
                          <button
                            key={preset.id}
                            onClick={() => updateFilter(filter.id, { defaultRange: preset.id })}
                            className={`px-2 py-1 text-xs rounded-md transition-colors ${
                              filter.defaultRange === preset.id
                                ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700'
                                : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
                            }`}
                          >
                            {preset.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <label className="text-xs text-surface-500 whitespace-nowrap">Source Table:</label>
                      <input
                        type="text"
                        value={filter.sourceTable || ''}
                        onChange={(e) => updateFilter(filter.id, { sourceTable: e.target.value })}
                        placeholder="e.g., flowexecution_xxx (for subqueries)"
                        className="flex-1 text-xs border border-surface-200 dark:border-surface-700 rounded px-2 py-1.5 bg-white dark:bg-surface-900"
                      />
                      <span className="text-[10px] text-surface-400 whitespace-nowrap" title="For complex queries with subqueries, specify the table name where this column exists">
                        &#9432;
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filterConfig.length === 0 && availableColumns.length > 0 && (
        <div className="text-center py-8">
          <FunnelIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
          <p className="text-surface-500 mb-2">No filters configured yet</p>
          <p className="text-sm text-surface-400">
            Click on a column above to add it as a filter
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Filter Tips</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
              <li><strong>Select/Multiselect:</strong> Best for categorical columns with limited unique values</li>
              <li><strong>Date Range:</strong> Use for date/timestamp columns to filter by time period</li>
              <li><strong>Text Search:</strong> For searching within text columns</li>
              <li><strong>Number:</strong> For numeric columns with range filtering</li>
              <li><strong>Source Table (for complex queries):</strong> If your SQL has subqueries and the filter column is only in the inner query, enter the table name (e.g., <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">flowexecution_xxx</code>) to inject the filter into the subquery</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
